import {
  createContext,
  useCallback,
  useMemo,
  useState,
  useContext,
  ReactNode,
} from "react";
import { startOfDay, subDays } from "date-fns";
import { PeriodType as ApiPeriodType } from "../../../common/data/service-proxies";

export type LocalPeriodType = "weekly" | "monthly";

export interface HarshDrivingReportContextValue {
  dateFrom: Date;
  dateTo: Date;
  periodType: LocalPeriodType;
  presetPeriod: unknown;
  isDownloading: boolean;
  apiPeriodType: ApiPeriodType | undefined;
  reportParams: {
    fromDate: Date;
    toDate: Date;
    periodType: ApiPeriodType | undefined;
  };
  setDateFrom: (d: Date) => void;
  setDateTo: (d: Date) => void;
  handlePeriodChange: (value: unknown) => void;
  setPresetPeriod: (v: unknown) => void;
}

export const HarshDrivingReportContext = createContext<HarshDrivingReportContextValue | null>(
  null,
);

export const useHarshDrivingReport = (): HarshDrivingReportContextValue => {
  const ctx = useContext(HarshDrivingReportContext);
  if (ctx == null) {
    throw new Error(
      "useHarshDrivingReport must be used within HarshDrivingReportProvider",
    );
  }
  return ctx;
};

interface HarshDrivingReportProviderProps {
  children: ReactNode;
}

export const HarshDrivingReportProvider = ({
  children,
}: HarshDrivingReportProviderProps) => {
  const [dateFrom, setDateFrom] = useState<Date>(() =>
    subDays(startOfDay(new Date()), 6),
  );
  const [dateTo, setDateTo] = useState<Date>(() => startOfDay(new Date()));
  const [periodType, setPeriodType] = useState<LocalPeriodType>("weekly");
  const [isDownloading] = useState(false);
  const [presetPeriod, setPresetPeriod] = useState<unknown>("");

  const apiPeriodType = useMemo<ApiPeriodType | undefined>(
    () =>
      periodType === "weekly" ? ApiPeriodType.Weekly : ApiPeriodType.Monthly,
    [periodType],
  );

  const reportParams = useMemo(
    () => ({
      fromDate: dateFrom,
      toDate: dateTo,
      periodType: apiPeriodType,
    }),
    [dateFrom, dateTo, apiPeriodType],
  );

  const handlePeriodChange = useCallback((value: unknown) => {
    const today = startOfDay(new Date());
    switch (value) {
      case "thisWeek":
        setPeriodType("weekly");
        setDateTo(today);
        setDateFrom(subDays(today, 6));
        break;
      case "lastWeek": {
        const end = subDays(today, 7);
        setPeriodType("weekly");
        setDateTo(end);
        setDateFrom(subDays(end, 6));
        break;
      }
      case "thisMonth":
        setPeriodType("monthly");
        setDateTo(today);
        setDateFrom(new Date(today.getFullYear(), today.getMonth(), 1));
        break;
      case "lastMonth": {
        const firstThisMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          1,
        );
        const lastMonthEnd = subDays(firstThisMonth, 1);
        setPeriodType("monthly");
        setDateTo(lastMonthEnd);
        setDateFrom(
          new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1),
        );
        break;
      }
      default:
        break;
    }
  }, []);

  const value = useMemo<HarshDrivingReportContextValue>(
    () => ({
      dateFrom,
      dateTo,
      periodType,
      presetPeriod,
      isDownloading,
      apiPeriodType,
      reportParams,
      setDateFrom,
      setDateTo,
      handlePeriodChange,
      setPresetPeriod,
    }),
    [
      dateFrom,
      dateTo,
      periodType,
      presetPeriod,
      isDownloading,
      apiPeriodType,
      reportParams,
      handlePeriodChange,
    ],
  );

  return (
    <HarshDrivingReportContext.Provider value={value}>
      {children}
    </HarshDrivingReportContext.Provider>
  );
};
